import * as Sentry from "@sentry/react";
import type {
	AudioModel,
	AudioGroupModel,
	AudioGroupWithAudiosModel,
} from "@coffitivity/shared";
import supabase from "@/lib/__shared__/supabase/client";
import { snakeCaseToKebabCase } from "@/lib/utils";

// recursively go through an objects keys and convert them from snake_case to camelCase
export function toCamelCase(obj: unknown): unknown {
	if (Array.isArray(obj)) {
		return obj.map((item) => toCamelCase(item));
	}
	if (obj && typeof obj === "object") {
		return Object.entries(obj).reduce(
			(acc, [key, value]) => {
				const camelCaseKey = key.replace(/_([a-z])/g, (_, letter) =>
					letter.toUpperCase(),
				);
				acc[camelCaseKey] = toCamelCase(value);
				return acc;
			},
			{} as Record<string, unknown>,
		);
	}
	return obj;
}

export interface IAudioRepository {
	getAdTracks(): Promise<Array<AudioModel>>;
	getFreeTracks(): Promise<Array<AudioModel>>;
	getGroupsByTrackId(_trackId: string): Promise<AudioGroupModel[]>;
	getPublicGroups(): Promise<AudioGroupModel[]>;
	getRecentlyAdded(_limit: number): Promise<Array<AudioModel>>;
	getTrack(_id: string): Promise<AudioModel>;
	getTrackBySlug(_slug: string): Promise<AudioModel>;
	getGroupAndTracksBySlug(
		_groupSlug: string,
	): Promise<AudioGroupWithAudiosModel>;
}

const audioWithBlendSettingsSqlSelect = `
  *,
  blend:blend_settings!blend_settings_blend_slug_fkey (
    volume,
    track:audios!blend_settings_track_slug_fkey (*)
  )
`;

const audioGroupWithTracksSqlSelect = `
  *,
  tracks:audio_group_items!audio_group_items_group_slug_fkey (
    position,
    track:audios!audio_group_items_audio_slug_fkey (
      *,
      blend:blend_settings!blend_settings_blend_slug_fkey (
        volume,
        track:audios!blend_settings_track_slug_fkey (
          *
        )
      )
    )
  )
`;

export class AudioRepository implements IAudioRepository {
	async getAdTracks(): Promise<Array<AudioModel>> {
		try {
			const adTracksPromise = () =>
				supabase.from("audios").select("*").eq("type", "ad");

			const { data, error } = await adTracksPromise();

			if (error) throw error;

			if (import.meta.env.VITE_ENV === "development") {
				return toCamelCase(
					data.filter((track) => track.slug === "coffitivity-test-ad"),
				) as Array<AudioModel>;
			}
			return toCamelCase(
				data.filter((track) => track.slug !== "coffitivity-test-ad"),
			) as Array<AudioModel>;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getFreeTracks(): Promise<Array<AudioModel>> {
		try {
			const { data, error } = await supabase
				.from("audio_groups")
				.select(audioGroupWithTracksSqlSelect)
				.eq("slug", "free")
				.single();

			if (error) throw error;
			if (!data || error) throw error || new Error("No free tracks found");

			const tracksWithPosition = (data as AudioGroupWithAudiosModel).tracks;

			// return the tracks sorted by position (null values last)
			// NOTE: we have to do this here because of the join. There's probably a way to do it in SQL but I'm not sure
			const sortedTracks = tracksWithPosition
				.sort((a, b) => {
					// Handle null values by putting them at the end
					if (a.position === null) return 1;
					if (b.position === null) return -1;
					// Otherwise sort by position ascending (1, 2, 3, etc.)
					return a.position - b.position;
				})
				.map((item) => item.track);

			return toCamelCase(sortedTracks) as Array<AudioModel>;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getGroupsByTrackId(_trackId: string): Promise<AudioGroupModel[]> {
		try {
			const { data, error } = await supabase
				.from("audio_group_items")
				.select(`
					group:audio_groups!audio_group_items_group_slug_fkey!inner (
						*
					),
					audio:audios!audio_group_items_audio_slug_fkey!inner (
						id
					)
				`)
				.eq("audio.id", _trackId)
				.is("group.user_id", null);

			if (error) throw error;

			const res = (data as unknown as Array<{ group: AudioGroupModel }>).map(
				(item) => item.group,
			);

			return toCamelCase(res) as AudioGroupModel[];
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getPublicGroups(): Promise<AudioGroupModel[]> {
		try {
			const { data, error } = await supabase
				.from("audio_groups")
				.select("*")
				.is("user_id", null)
				.order("position", { ascending: true });

			if (error) throw error;

			return toCamelCase(data) as AudioGroupModel[];
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getRecentlyAdded(_limit = 10): Promise<Array<AudioModel>> {
		try {
			const { data, error } = await supabase
				.from("audios")
				.select(audioWithBlendSettingsSqlSelect)
				.order("release_date", { ascending: false })
				.limit(_limit);

			if (error) throw error;
			return toCamelCase(data) as Array<AudioModel>;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getTrack(_id: string): Promise<AudioModel> {
		try {
			const { data, error } = await supabase
				.from("audios")
				.select(audioWithBlendSettingsSqlSelect)
				.eq("id", _id)
				.single();

			if (error || !data) throw error || new Error("Track not found");
			return toCamelCase(data) as AudioModel;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getTrackBySlug(_slug: string): Promise<AudioModel> {
		try {
			const sanitizedSlug = snakeCaseToKebabCase(_slug.trim());
			const { data, error } = await supabase
				.from("audios")
				.select(audioWithBlendSettingsSqlSelect)
				.eq("slug", sanitizedSlug)
				.single();

			if (error || !data) throw error || new Error("Track not found");
			return toCamelCase(data) as AudioModel;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}

	async getGroupAndTracksBySlug(
		_groupSlug: string,
	): Promise<AudioGroupWithAudiosModel> {
		try {
			const sanitizedSlug = snakeCaseToKebabCase(_groupSlug.trim());

			const { data, error } = await supabase
				.from("audio_groups")
				.select(audioGroupWithTracksSqlSelect)
				.eq("slug", sanitizedSlug)
				.single();

			if (error || !data) throw error || new Error("Group not found");

			return toCamelCase(data) as AudioGroupWithAudiosModel;
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(error);
		}
	}
}
